import { useState, useEffect, useMemo, useCallback } from 'react';
import { Layer, PickInfo } from 'deck.gl';
import { ConvertHexToRGBA, selectedColor } from '../../../../utils/common';
import useLegendStore from '../../../../store/legendStore';
import { useMarkerStore, useActiveMarker } from '../../../../store/markerStore';
import { IMarker } from '../../../../types/marker';
import { useMapLayerStore } from '../../../../store/layerStore';
import { usePointStore } from '../../../../store/pointStore';
import { IInsituPoint } from '../../../../api/endpoints/FileEndPoint';
import { ILabelLayer, IPolygonLayer } from '../../../../types/map';
import useMapStore from '../../../../store/mapStore';
import { toFixed } from '../../../../utils/toFixed';
import {
  DatasetZoneLayer,
  FeatureCollectionLayer,
  GlobalDatasetZoneLayer,
  IUploadedLayer,
  SelectedZoneLayer,
  SurfaceMapLayer,
} from '../Layer/DatasetZoneLayer';
import {
  ExpandInSituLayer,
  FWEPointLayer,
  InSituPointLayer,
  LabelLayer,
  MarkerLayer,
} from '../Layer/MarkerLayer';
import { MeshWMSLayer } from '../Layer/MeshWMSLayer';
import { IWmsProps } from '../Layer/DatasetMesh';

interface MapLayersReturn {
  layer: any[];
}

/**
 * Custom hook to manage map layers
 *
 */
export function useMapLayers(
  meshUrl: IWmsProps | undefined,
  setMeshUrl: (item?: IWmsProps) => void,
  expandedPoints: IInsituPoint[] | null
): MapLayersReturn {
  const [layer, setLayer] = useState<any[]>([]);

  const markers = useMarkerStore((state) => state.markers);
  const point = useActiveMarker();
  const updateMarker = useMarkerStore((state) => state.updateMarker);

  const setUpdatedBounds = useMapStore((state) => state.setUpdatedBounds);
  const setDragPan = useMapStore((state) => state.setDragPan);
  const setCoordsMoved = useMapStore((state) => state.setCoordsMoved);

  const showDatasetZones = useLegendStore((state) => state.showDatasetZones);
  const showGlobalShapeFile = useLegendStore(
    (state) => state.showGlobalShapeFile
  );
  const showLabelLayer = useLegendStore((state) => state.showLabelLayer);
  const uploadedFeatures = useLegendStore((state) => state.uploadedFeatures);
  const showSurfaceMap = useLegendStore((state) => state.showSurfaceMap);
  const visibleUploads = useLegendStore((state) => state.visibleUploads);
  const uploadedShapefile = useLegendStore((state) => state.uploadedShapefile);
  const showUploadedShapefile = useLegendStore(
    (state) => state.showUploadedShapefile
  );
  const setUploadingShapefile = useLegendStore(
    (state) => state.setUploadingShapefile
  );
  const showInSituPoints = useLegendStore((state) => state.showInSituPoints);
  const showFWEPoints = useLegendStore((state) => state.showFWEPoints);

  const datasetZones = useMapLayerStore((state) => state.datasetZones);
  const fweModelDomains = useMapLayerStore((state) => state.fweModelDomains);
  const globalShapeFile = useMapLayerStore((state) => state.globalShapeFile);

  const inSituPoints = usePointStore((state) => state.inSituPoints);
  const fwePoints = usePointStore((state) => state.fwePoints);

  const onDrag = (info: PickInfo<IMarker>, e: any) => {};
  const onDragStart = (info: PickInfo<IMarker>, e: any) => {
    setDragPan(false);
  };
  const onDragEnd = (info: PickInfo<IMarker>, e: any) => {
    if (
      point &&
      info.coordinate &&
      info.coordinate.length === 2 &&
      info.object &&
      info.object.active
    ) {
      updateMarker(point.id, {
        coordinatesToMove: [
          toFixed(info.coordinate[0], 6),
          toFixed(info.coordinate[1], 6),
        ],
      });
      setCoordsMoved(true);
    }
    setDragPan(true);
  };

  /**
   * Creates dataset zone and FWE layers based on visibility status
   */
  const createBasicLayers = useCallback((): IPolygonLayer[] => {
    const polygonLayers: IPolygonLayer[] = [];

    if (showDatasetZones) {
      polygonLayers.push(...datasetZones);
    }

    if (showFWEPoints) {
      polygonLayers.push(...fweModelDomains);
    }

    point?.shapeFiles.forEach((shape) => {
      if (shape.IsDisplay && shape.data) {
        polygonLayers.push(...shape.data);
      }
    });

    return polygonLayers;
  }, [
    showDatasetZones,
    showFWEPoints,
    datasetZones,
    fweModelDomains,
    point?.shapeFiles,
  ]);

  /**
   * Creates selected zone layer with appropriate coloring
   */
  const createSelectedZoneLayer = useCallback((): IPolygonLayer[] => {
    const selectedPolygonLayer: IPolygonLayer[] = [];

    if (!point?.dataset) return selectedPolygonLayer;

    const lineColor = ConvertHexToRGBA(selectedColor);
    const filledColor = ConvertHexToRGBA(selectedColor, 0.2);

    datasetZones.forEach((zone) => {
      if (zone.id === point.dataset) {
        selectedPolygonLayer.push({
          id: zone.id,
          coordinates: zone.coordinates,
          lineColor: lineColor,
          color: filledColor,
        });
      }
    });

    return selectedPolygonLayer;
  }, [datasetZones, point?.dataset]);

  /**
   * Retrieves surface map data based on the active point
   */
  const getSurfaceMapData = useCallback((): {
    data: IPolygonLayer[];
    shouldUpdateBounds: boolean;
  } => {
    let surfaceMapLayerData: IPolygonLayer[] = [];
    let shouldUpdateBounds = false;

    if (
      point &&
      showSurfaceMap &&
      point.surfaceMaps.selectedLevel1 !== '' &&
      point.surfaceMaps.selectedLevel2 !== ''
    ) {
      const surfaceMap = point.surfaceMaps.overlays.find(
        (o) =>
          o.Level1 === point.surfaceMaps.selectedLevel1 &&
          o.Level2 === point.surfaceMaps.selectedLevel2
      );

      if (surfaceMap?.data) {
        surfaceMapLayerData = surfaceMap.data;
        shouldUpdateBounds = true;
      }
    }

    return { data: surfaceMapLayerData, shouldUpdateBounds };
  }, [point, showSurfaceMap]);

  /**
   * Creates labels for displayed markers
   */
  const createPointLabels = useMemo((): ILabelLayer[] => {
    if (!showLabelLayer) return [];

    return markers.map(
      (point): ILabelLayer => ({
        color: [142, 7, 77],
        name: point.label,
        coordinates: point.coordinates,
        active: point.active,
        textSize: 24,
        textAnchor: 'middle',
      })
    );
  }, [markers, showLabelLayer]);

  /**
   * AppContainer function to update all map layers
   */
  const updateMapLayers = useCallback((): void => {
    const features: IUploadedLayer[] =
      showUploadedShapefile && uploadedFeatures ? uploadedFeatures : [];

    if (showUploadedShapefile && uploadedFeatures) {
      setUploadingShapefile(false);
    }

    const polygonLayers = createBasicLayers();
    const selectedPolygonLayer = createSelectedZoneLayer();
    const { data: surfaceMapLayerData, shouldUpdateBounds } =
      getSurfaceMapData();
    setUpdatedBounds(shouldUpdateBounds);

    const mapLayersArray = [
      SurfaceMapLayer(surfaceMapLayerData) as Layer<any, any>,
      MeshWMSLayer({ ...meshUrl } as IWmsProps, setMeshUrl) as any,
      DatasetZoneLayer(polygonLayers) as Layer<any, any>,
      GlobalDatasetZoneLayer(
        showGlobalShapeFile ? globalShapeFile : []
      ) as Layer<any, any>,
      SelectedZoneLayer(selectedPolygonLayer) as Layer<any, any>,
      FWEPointLayer(showFWEPoints ? fwePoints : []) as Layer<any, any>,
      ExpandInSituLayer(
        showInSituPoints && expandedPoints ? expandedPoints : []
      ),
      InSituPointLayer(showInSituPoints ? inSituPoints : []) as Layer<any, any>,
      ...(FeatureCollectionLayer(features, visibleUploads) as Array<
        Layer<any, any>
      >),
    ];

    const mapLayers = mapLayersArray.filter(Boolean) as any[];

    const featureCollectionLayers = FeatureCollectionLayer(
      features,
      visibleUploads
    );

    const graticuleLabels: ILabelLayer[] = [];
    const pointLabels = createPointLabels;

    const labelLayer = LabelLayer([...graticuleLabels, ...pointLabels]);

    const markerLayers = showLabelLayer
      ? MarkerLayer(markers, onDrag, onDragStart, onDragEnd)
      : [];

    setLayer([
      ...mapLayers,
      ...(featureCollectionLayers as any[]),
      labelLayer as any,
      ...(markerLayers as any[]),
    ]);
  }, [
    showDatasetZones,
    showUploadedShapefile,
    showLabelLayer,
    showFWEPoints,
    showInSituPoints,
    showGlobalShapeFile,
    markers,
    datasetZones,
    point,
    uploadedShapefile,
    uploadedFeatures,
    visibleUploads,
    fwePoints,
    inSituPoints,
    fweModelDomains,
    meshUrl,
    globalShapeFile,
    expandedPoints,
  ]);

  useEffect(() => {
    updateMapLayers();
  }, [updateMapLayers]);

  return { layer };
}
